import LegalPage from "../components/LegalPage";

/** /privacy — the privacy policy, adapted from Automattic's (CC BY-SA). */
const PrivacyPage = () => (
  <LegalPage title="Privacy Policy" updated="September 14, 2026">
    <p>
      This policy describes what information Splatboard LLC ("Splatboard," "we")
      collects when you use StreamLover — the streamlover.app website and the
      StreamLover mobile apps (the "Services") — how we use it, and the choices
      you have. The short version: we collect what we need to run a radio
      service, we don't sell your personal information, and we don't show you
      ads.
    </p>

    <h2>Information you provide</h2>
    <p>We collect information you give us directly:</p>
    <ul>
      <li>
        <strong>Account details.</strong> Your email address and the username
        you choose. Signing in works with one-time codes sent to your email, so
        we never store a password.
      </li>
      <li>
        <strong>Things you post.</strong> Comments, reactions such as hearts,
        and anything else you add to your profile.
      </li>
      <li>
        <strong>Messages to us.</strong> If you email us or send feedback, we
        keep the correspondence.
      </li>
    </ul>

    <h2>Information collected automatically</h2>
    <p>Like most online services, we collect some information automatically:</p>
    <ul>
      <li>
        <strong>Usage and listening activity.</strong> Which stations, shows,
        and recordings you play, and how you use the app's features. This powers
        things like your recently-played list and helps us understand what's
        working.
      </li>
      <li>
        <strong>Log and device information.</strong> IP address, request times,
        browser or device type, operating system, and app version — standard
        server logs and diagnostics.
      </li>
      <li>
        <strong>Crash and error reports.</strong> When the app or site hits a
        problem, a report about the error and the state of the app may be sent
        to us so we can fix it.
      </li>
      <li>
        <strong>Push tokens.</strong> If you turn on notifications, we store the
        device token needed to deliver them.
      </li>
    </ul>

    <h2>Cookies</h2>
    <p>
      The website uses cookies to keep you signed in and to remember preferences
      like your theme choice. We don't use third-party advertising or cross-site
      tracking cookies.
    </p>

    <h2>How we use information</h2>
    <p>We use the information we collect to:</p>
    <ul>
      <li>provide and personalize the Services;</li>
      <li>
        send things you've asked for, like sign-in codes and show notifications
        you've turned on;
      </li>
      <li>fix problems, monitor performance, and improve the Services;</li>
      <li>
        protect the Services and our users — for example, detecting abuse or
        enforcing our <a href="/terms">Terms of Service</a>
        {";"}
      </li>
      <li>communicate with you about the Services.</li>
    </ul>

    <h2>Sharing</h2>
    <p>
      We don't sell our users' personal information, and we don't share it with
      third parties for their advertising. We share information only in these
      limited circumstances:
    </p>
    <ul>
      <li>
        <strong>Service providers.</strong> Vendors that help us run the
        Services and process information on our behalf: cloud hosting, email
        delivery (for sign-in codes and notifications), crash and error
        monitoring, and push and realtime message delivery through providers
        such as Apple and Google.
      </li>
      <li>
        <strong>Legal requirements.</strong> When we believe in good faith that
        disclosure is required by law, or necessary to protect the rights,
        property, or safety of Splatboard, our users, or the public.
      </li>
      <li>
        <strong>Business transfers.</strong> If Splatboard is involved in a
        merger, acquisition, or sale of assets, user information may be
        transferred as part of that transaction, subject to this policy.
      </li>
      <li>
        <strong>Aggregated information.</strong> We may share statistics that
        don't identify you — for example, how many listeners a show had.
      </li>
    </ul>

    <h2>Public parts of the Services</h2>
    <p>
      Some of what you do on StreamLover is visible to others by design: your
      username, and the comments and reactions you post, appear to other users.
      Please keep that in mind when deciding what to share.
    </p>

    <h2>Retention</h2>
    <p>
      We keep information for as long as it's needed to provide the Services and
      for legitimate business purposes like security, backups, and complying
      with our legal obligations. When you delete your account, we delete or
      de-identify your personal information within a reasonable period, except
      where we're required to retain it.
    </p>

    <h2>Security</h2>
    <p>
      We work to protect your information against unauthorized access, use,
      alteration, and destruction — for example, traffic to the Services is
      encrypted in transit. No online service can guarantee perfect security,
      but we take it seriously.
    </p>

    <h2>Your choices and rights</h2>
    <ul>
      <li>
        <strong>Access, correction, deletion.</strong> You can update your
        profile in the app, and you can ask us to access, correct, or delete the
        personal information we hold about you by emailing{" "}
        <a href="mailto:legal@streamlover.app">legal@streamlover.app</a>.
      </li>
      <li>
        <strong>Notifications.</strong> Turn push notifications off any time in
        the app or in your device settings.
      </li>
      <li>
        <strong>Cookies.</strong> Your browser lets you block or delete cookies,
        though signing in to the website won't work without them.
      </li>
    </ul>
    <p>
      Depending on where you live, you may have additional privacy rights under
      laws like the California Consumer Privacy Act or the EU's General Data
      Protection Regulation. We honor requests to exercise those rights at the
      address above, and we won't discriminate against you for making one.
    </p>

    <h2>Children</h2>
    <p>
      The Services aren't directed to children under 13, and we don't knowingly
      collect personal information from them. If you believe a child under 13
      has provided us personal information, contact us and we'll delete it.
    </p>

    <h2>Changes to this policy</h2>
    <p>
      We may update this policy from time to time. If a change is material,
      we'll take reasonable steps to let you know before it takes effect. The
      date at the top always reflects the latest version.
    </p>

    <h2>Contact</h2>
    <p>
      Questions about privacy? Email{" "}
      <a href="mailto:legal@streamlover.app">legal@streamlover.app</a>.
    </p>

    <p className="legal__credit">
      Adapted from Automattic's{" "}
      <a href="https://github.com/Automattic/legalmattic">
        open-source legal documents
      </a>
      , available under a{" "}
      <a href="https://creativecommons.org/licenses/by-sa/4.0/">
        Creative Commons Attribution-ShareAlike 4.0
      </a>{" "}
      license. This document is likewise licensed under CC BY-SA 4.0.
    </p>
  </LegalPage>
);

export default PrivacyPage;
